import { Platform } from 'react-native';
import * as bridge from '@thermenresorts/bridge';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import Constants from 'src/core/utils/Constants';
import Translations from 'src/core/utils/Translations';
import ValidationService from 'src/data/services/ValidationService';
import { useServicesStore } from 'src/domain/stores/useServicesStore';

type WebMessage = typeof Constants.bridgeMessageRedirect | typeof Constants.bridgeMessageReservationFlowOpen;

const initialState = {
	pendingUrl: null as string | null,
	description: '',
};

export const useLeaveAppStore = create(
	combine({ ...initialState }, (set, get) => {
		const reset = () => set({ ...initialState });

		const openExternally = ({ url }: { url: string }) => useServicesStore.getState().openUrlExternally({ url });

		// On web the app sits in an iframe, so the host page navigates instead of an app being left.
		const leave = ({ url }: { url: string }) => {
			if (Platform.OS === Constants.platformWeb) {
				bridge.sendMessage(Constants.bridgeChannel, Constants.bridgeMessageRedirect, { url });

				return;
			}

			openExternally({ url });
		};

		const confirmLeaving = ({
			url,
			description,
			webMessage,
		}: {
			url: string;
			description?: string;
			webMessage?: WebMessage;
		}) => {
			// A phone or mail link hands over to an app that confirms on its own.
			if (!ValidationService.isWebUrl(url)) {
				openExternally({ url });

				return;
			}

			// On web a reservation flow opens as a layer over the drawer, so nothing is left behind.
			if (Platform.OS === Constants.platformWeb && webMessage === Constants.bridgeMessageReservationFlowOpen) {
				bridge.sendMessage(Constants.bridgeChannel, webMessage, { url });

				return;
			}

			set({ pendingUrl: url, description: description ?? Translations.leaveAppDescription });
		};

		// The modal keeps fading out after the url is gone, so the description stays until the next url arrives.
		const close = () => set({ pendingUrl: null });

		const cancel = () => close();

		const confirm = () => {
			const { pendingUrl } = get();

			close();

			if (pendingUrl === null) {
				return;
			}

			leave({ url: pendingUrl });
		};

		return { confirmLeaving, cancel, confirm, reset };
	}),
);
